import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useState, useMemo, type HTMLAttributes } from "react";
import { useMunicipalitySearchQuery } from "Api/Queries/Merchants/useMunicipalitySearchQuery";
import { useDebouncedSearch } from "Hooks/useDebouncedSearch";
import type { MunicipalityDto } from "Infrastructure/Api/Api";

const isOptionEqualToValue = (
  option: MunicipalityDto,
  value: MunicipalityDto,
) => option.name === value.name;

const getOptionLabel = ({ name }: MunicipalityDto) => name;

const renderOption = (
  props: HTMLAttributes<HTMLLIElement> & { key: string },
  { name, facilityCount }: MunicipalityDto,
) => (
  <Box component="li" {...props}>
    <Typography component="span">{name}</Typography>
    <Typography component="span" color="text.secondary" ml={0.5}>
      ({facilityCount})
    </Typography>
  </Box>
);

type Props = {
  label: string;
  value: string | number | "";
  onChange: (value: string | number | "") => void;
  placeholder?: string;
  disabled?: boolean;
  accessibilityLabel?: string;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  multiple?: boolean;
};

export const MunicipalityAutocomplete = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  accessibilityLabel,
  inputValue: controlledInputValue,
  onInputChange,
  multiple = false,
}: Props) => {
  const [internalInputValue, setInternalInputValue] = useState("");
  // Track the selected municipality object to persist it across search changes
  const [selectedMunicipalityState, setSelectedMunicipalityState] =
    useState<MunicipalityDto | null>(null);
  const theme = useTheme();
  const { setSearchTerm, debouncedSearchTerm } = useDebouncedSearch();

  const inputValue =
    controlledInputValue !== undefined
      ? controlledInputValue
      : internalInputValue;

  // Fetch initial options without filter, then filtered options as user types
  // Pass undefined when searchTerm is empty to fetch all municipalities initially
  const { data: searchResults, isLoading } = useMunicipalitySearchQuery(
    debouncedSearchTerm === "" ? undefined : debouncedSearchTerm,
    true, // Always enabled - fetch all initially, then filtered
  );

  const apiOptions = searchResults?.items ?? [];

  // Merge selected municipality into options to ensure it's always available
  const options = useMemo(() => {
    if (
      !selectedMunicipalityState ||
      apiOptions.some((opt) => opt.name === selectedMunicipalityState.name)
    ) {
      return apiOptions;
    }
    return [selectedMunicipalityState, ...apiOptions];
  }, [apiOptions, selectedMunicipalityState]);

  // Use the stored selected municipality, or find it in options if value changed externally
  const selectedMunicipality = useMemo(() => {
    if (!value || value === "") {
      return null;
    }
    // If we have a stored selection matching the value, use it
    if (selectedMunicipalityState?.name === value) {
      return selectedMunicipalityState;
    }
    // Otherwise try to find it in options (for externally set values)
    return options.find((municipality) => municipality.name === value) || null;
  }, [value, selectedMunicipalityState, options]);

  const handleInputChange = (_event: unknown, newInputValue: string) => {
    if (controlledInputValue === undefined) {
      setInternalInputValue(newInputValue);
    }
    setSearchTerm(newInputValue);
    onInputChange?.(newInputValue);
  };

  const handleChange = (
    _event: unknown,
    newValue: MunicipalityDto | null | Array<MunicipalityDto>,
  ) => {
    if (multiple) {
      // Multiple selection (for future use)
      const municipalities = newValue as Array<MunicipalityDto>;
      const selected = municipalities[0] ?? null;
      setSelectedMunicipalityState(selected);
      onChange(selected?.name ?? "");
    } else {
      // Single selection
      const municipality = newValue as MunicipalityDto | null;
      setSelectedMunicipalityState(municipality);
      onChange(municipality?.name ?? "");
    }
  };

  const hasValue = value !== "" && value !== null && value !== undefined;

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      variant="outlined"
      placeholder={hasValue ? undefined : placeholder || label}
      slotProps={{
        htmlInput: {
          ...params.inputProps,
          "aria-label": accessibilityLabel || label,
        },
      }}
      sx={{
        width: "100%",
        backgroundColor: "grey.300",
        borderRadius: "20px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        "& .MuiOutlinedInput-root": {
          height: 40,
          paddingRight: `${theme.spacing(hasValue ? 15 : 8)} !important`,
          "& fieldset": {
            border: "none",
          },
          "&:hover fieldset": {
            border: "none",
          },
          "&.Mui-focused fieldset": {
            border: "none",
          },
          "& input": {
            padding: `${theme.spacing(1.5)} ${theme.spacing(3)} !important`,
            color: "text.primary",
            fontSize: 16,
            fontWeight: 500,
            height: "auto",
          },
        },
        "&:hover": {
          backgroundColor: "grey.400",
        },
        "&.Mui-focused": {
          backgroundColor: "grey.400",
        },
        "&.Mui-disabled": {
          backgroundColor: "grey.500",
          opacity: 0.6,
        },
      }}
    />
  );

  return (
    <Box position="relative" display="inline-block" width={["100%", "auto"]}>
      <Autocomplete
        multiple={multiple}
        options={options}
        disabled={disabled}
        value={selectedMunicipality}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        loading={isLoading}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        isOptionEqualToValue={isOptionEqualToValue}
        filterOptions={(x) => x} // Disable built-in filtering since we're using server-side search
        renderInput={renderInput}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-endAdornment": {
            right: hasValue ? 36 : 8,
          },
        }}
      />
    </Box>
  );
};
